/**
 * BottomTabBar
 * Four icon-only tabs: Home · Agent · Activities · Me (no More menu).
 *
 * The legacy "Optimizer" tab was removed when optimization moved to the
 * AI Agent (`/agent`). The agent tab now occupies that slot.
 */
import { Home, Sparkles, Clock, User } from 'lucide-react';
import React from 'react';

import type { MobileTab } from '../../hooks/useMobileNavigation';

interface TabDef {
  id: MobileTab;
  label: string;
  Icon: React.FC<{ size?: number; strokeWidth?: number; color?: string }>;
}

const MAIN_TABS: TabDef[] = [
  { id: 'home', label: 'Home', Icon: Home },
  { id: 'optimizer', label: 'Agent', Icon: Sparkles },
  { id: 'history', label: 'Activities', Icon: Clock },
  { id: 'me', label: 'Me', Icon: User },
];

interface BottomTabBarProps {
  activeTab: MobileTab;
  onTabPress: (tab: MobileTab) => void;
}

const BottomTabBar: React.FC<BottomTabBarProps> = ({ activeTab, onTabPress }) => {
  return (
    <nav
      aria-label="Main navigation"
      style={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        minHeight: 48,
        paddingTop: 8,
        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
        boxSizing: 'border-box',
        background: 'var(--ggj-nav-bg)',
        borderTop: '1px solid var(--ggj-border)',
        zIndex: 200,
      }}
    >
      {MAIN_TABS.map(({ id, label, Icon }) => {
        const isActive = activeTab === id;
        const iconColor = isActive
          ? 'var(--vscode-primary)'
          : 'var(--ggj-tab-inactive)';
        return (
          <button
            key={id}
            type="button"
            aria-label={label}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => onTabPress(id)}
            style={{
              flex: 1,
              position: 'relative' as const,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '8px 4px',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              minHeight: 48,
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 10,
                background: isActive ? 'var(--brand-accent-bg)' : 'transparent',
                color: iconColor,
                transition: 'background 0.18s ease, color 0.18s ease',
              }}
            >
              <Icon
                size={24}
                strokeWidth={isActive ? 2.2 : 1.8}
                color={iconColor}
              />
            </span>
            {isActive && (
              <span
                style={{
                  position: 'absolute',
                  bottom: 6,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 24,
                  height: 2,
                  borderRadius: 1,
                  background: 'var(--vscode-primary)',
                }}
              />
            )}
          </button>
        );
      })}
    </nav>
  );
};

export default BottomTabBar;
